// Application Constants

// Admin Credentials
export const ADMIN_CREDENTIALS = {
  EMAIL: "sgupta@gmail.com",
  PASSWORD: "1234",
  NAME: "Dr. Gupta",
  USER_ID: "admin",
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  IS_LOGGED_IN: "isLoggedIn",
  USER_EMAIL: "userEmail",
  IS_ADMIN: "isAdmin",
  HOSPITAL_NAME: "hospitalName",
  USER_ID: "userId",
  ROLE: "userRole",
  AUTH_TOKEN: "authToken", // Add token storage key
} as const;

// Filter Options
export const FILTER_OPTIONS = {
  ALL: "all",
  RISK_LEVELS: {
    POOR: "poor",
    AVERAGE: "average",
    GOOD: "good",
  },
  AGE_GROUPS: {
    YOUNG: "18-30",
    MIDDLE: "31-50",
    SENIOR: "51+",
  },
  GENDERS: {
    MALE: "male",
    FEMALE: "female",
  },
} as const;

// Sort Options
export const SORT_OPTIONS = {
  DIRECTIONS: {
    ASC: "asc",
    DESC: "desc",
  },
  FIELDS: {
    NAME: "name",
    AGE: "age",
    LAST_SYNC: "lastSync",
    RISK_LEVEL: "riskLevel",
    BLOOD_PRESSURE: "bloodPressure",
  },
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 1000,
  RECORDS_PER_PAGE: 10,
} as const;

// Vital Signs
export const VITAL_SIGNS = {
  HEART_RATE: "HEART_RATE",
  BLOOD_PRESSURE: "BLOOD_PRESSURE",
  BLOOD_OXYGEN: "BLOOD_OXYGEN",
  RANGES: {
    HEART_RATE: { MIN: 60, MAX: 100, CHART_MIN: 0, CHART_MAX: 200 },
    BLOOD_PRESSURE: {
      SYSTOLIC_HIGH: 140,
      DIASTOLIC_HIGH: 90,
      SYSTOLIC_ELEVATED: 120,
      DIASTOLIC_ELEVATED: 80,
    },
    BLOOD_OXYGEN: { MIN: 95, CRITICAL: 90, CHART_MIN: 70, CHART_MAX: 105 },
  },
} as const;

// Risk Levels
export const RISK_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  THRESHOLDS: {
    LOW: 30,
    MEDIUM: 60,
    HIGH: 100,
  },
} as const;

// Survey Constants
export const SURVEY = {
  UNITS: {
    HEIGHT: {
      CM: "cm",
      FT_IN: "ft_in",
    },
    WEIGHT: {
      KG: "kg",
      POUNDS: "pounds",
    },
  },
  CONTACT_TYPES: {
    EMAIL: "email",
    MOBILE: "mobile",
  },
  VALIDATION: {
    EMAIL_REGEX: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    MOBILE_REGEX: /^[6-9]\d{9}$/,
    AGE_MAX_DIGITS: 2,
    WEIGHT_MAX_DIGITS: 3,
    HEIGHT_MAX_DIGITS: {
      FEET: 2,
      INCHES: 2,
      CM: 3,
    },
  },
  CONVERSIONS: {
    CM_PER_FOOT: 30.48,
    CM_PER_INCH: 2.54,
    KG_PER_POUND: 0.453592,
  },
} as const;

// UI Messages
export const MESSAGES = {
  LOADING: {
    PATIENTS: "Loading patients...",
    HEALTH_METRICS: "Loading health metrics...",
    REPORT: "Loading Report...",
    SUMMARY: "Loading summary...",
    SIGNING_IN: "Signing in...",
  },
  ERRORS: {
    FETCH_PATIENTS: "Failed to fetch patients",
    INVALID_CREDENTIALS: "Invalid email or password",
    LOGIN_FAILED: "Login failed. Please check your connection and try again.",
    EMAIL_EXISTS: "This email is already registered, please use another email",
    UNKNOWN_ERROR: "Unknown error",
    NO_DATA: "No data available",
  },
  SUCCESS: {
    NOTE_SAVED: "Note saved successfully",
  },
  EMPTY_STATES: {
    NO_PATIENTS_ADMIN: "No patients found",
    NO_PATIENTS_HOSPITAL: "No patients registered",
    NO_PATIENTS_FILTERED: "No patients found matching your criteria",
  },
  VALIDATION: {
    REQUIRED_FIELD: "This field is required",
    INVALID_EMAIL: "Please enter a valid email address",
    INVALID_MOBILE: "Please enter a valid 10-digit mobile number",
    PASSWORD_MIN_LENGTH: "Password must be at least 8 characters",
    EMAIL_BLOCKED: "This email address is not allowed",
  },
} as const;

// File Paths
export const FILE_PATHS = {
  IMAGES: {
    HELIUS_LOGO: "/images/HeliusLogoWithText.svg",
  },
} as const;

// CSS Classes
export const CSS_CLASSES = {
  BUTTONS: {
    PRIMARY:
      "bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
    SECONDARY: "bg-gray-100 hover:bg-gray-200 text-gray-700",
    DANGER: "bg-red-600 text-white hover:bg-red-700",
    LOADING: "bg-gray-400 text-gray-200 cursor-not-allowed",
  },
  CARDS: {
    BASE: "bg-white rounded-xl shadow-sm",
    WITH_BORDER: "bg-white rounded-xl border border-gray-200",
    OVERFLOW_HIDDEN: "overflow-hidden",
  },
  INPUTS: {
    BASE: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
    ERROR: "border-red-300 focus:ring-red-500",
  },
  TEXT: {
    ERROR: "text-red-600 text-sm",
    SUCCESS: "text-green-600 text-sm",
    MUTED: "text-gray-500 text-sm",
  },
  RISK_BADGES: {
    HIGH: "bg-red-100 text-red-800",
    MEDIUM: "bg-yellow-100 text-yellow-800",
    LOW: "bg-green-100 text-green-800",
  },
  LAYOUTS: {
    CONTAINER: "mx-auto px-4 sm:px-6 lg:px-8 py-8",
    FLEX_CENTER: "flex items-center justify-center",
    FULL_HEIGHT: "min-h-screen",
  },
} as const;

// Default Values
export const DEFAULTS = {
  HOSPITAL_NAME: "Hospital User",
} as const;

export type FilterOption = (typeof FILTER_OPTIONS)[keyof typeof FILTER_OPTIONS];
export type RiskLevel = (typeof RISK_LEVELS)[keyof typeof RISK_LEVELS];
export type SortDirection =
  (typeof SORT_OPTIONS.DIRECTIONS)[keyof typeof SORT_OPTIONS.DIRECTIONS];
